import { memo, useEffect, useState, useRef } from "react";
import { useSortable } from "@dnd-kit/react/sortable";
import { Check, Pause, MoreVertical, Clock, Bell, GripVertical, Anchor, Circle, CheckCircle2, CornerDownRight, ChevronRight, ChevronDown } from "lucide-react";
import { Highlight } from "./tasks/Highlight";
import { SubtaskTree } from "./SubtaskTree";
import { SubtaskDates } from "./SubtaskDates";
import { flattenSubs } from "../lib/subtaskTree";
import { NestDropDot } from "../lib/nestDrop";
import { Maximize2, Minimize2 } from "lucide-react";
import { GecikmeIsareti } from "./tasks/TaskColor";
import { renkStili } from "../lib/taskColors";

// AŞAMA 9 (Adım 2) — alt görev dışarı alındığında listede kalan yer tutucu.
const DetachedSubtaskPlaceholder = ({ taskId, sub, displayNumber, onDock, onContextMenu }) => (
  <div
    data-testid={`subtask-detached-placeholder-${taskId}-${sub.id}`}
    onContextMenu={onContextMenu}
    className="rounded border border-dashed border-violet-400/50 bg-violet-500/5 px-1.5 py-1 flex items-center gap-2"
  >
    <Maximize2 className="h-3.5 w-3.5 text-violet-300 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-xs font-mono text-violet-200/90 truncate">
        {displayNumber != null ? `${displayNumber}. ` : ""}{sub.text}
      </div>
      <div className="text-[10px] font-mono text-sertex-textMuted">Bu alt görev yüzen pencerede açık</div>
    </div>
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onDock(); }}
      data-testid={`subtask-redock-${taskId}-${sub.id}`}
      title="Listeye geri al"
      style={{ pointerEvents: "auto", touchAction: "manipulation" }}
      className="shrink-0 px-1.5 py-0.5 rounded border border-violet-400/40 text-violet-200 hover:bg-violet-500/15 hud-text flex items-center gap-1 transition-colors"
    >
      <Minimize2 className="h-3 w-3" /> GERİ AL
    </button>
  </div>
);

const subIsOverdue = (s) => {
  if (s.done || s.status === "done" || s.status === "paused") return false;
  if (s.status === "overdue") return true;
  if (!s.due_date) return false;
  return new Date(s.due_date) < new Date();
};

const subtaskStyle = (s) => {
  if (s.done || s.status === "done") {
    return { border: "border-emerald-400/60", bg: "bg-emerald-500/10", accent: "text-emerald-300" };
  }
  if (s.status === "paused") {
    return { border: "border-yellow-400/60", bg: "bg-yellow-500/10", accent: "text-yellow-300" };
  }
  if (subIsOverdue(s)) {
    return { border: "border-rose-500/60", bg: "bg-rose-500/10", accent: "text-rose-300" };
  }
  return { border: "border-sertex-cyan/30", bg: "", accent: "text-sertex-cyan" };
};

export const SUBTASK_SIZE_KEY_PREFIX = "sertex_subtask_size_";
export const SUBTASK_COLLAPSE_KEY_PREFIX = "sertex_subtask_collapsed_";

// ============ SUBTASK ROW (id-based, recursive, drag-and-drop) ============
// Her satır: kendi içeriği + (varsa) iç içe çocukları (SubtaskTree ile).
function SubtaskRowBase({ sub, index = 0, group, parentId = null, depth = 0, ctx }) {
  // AŞAMA 9 (Adım 1) — `type`/`accept` gruba sabit: satır yalnızca KENDİ
  // kardeşleri arasında yer değiştirir, seviye değiştirme mümkün değildir.
  // AŞAMA 9 (Adım 2) — bu alt görev dışarı alındıysa listede yalnızca yer
  // tutucu görünür; indeksi korunur ("GERİ AL" ile eski yerine döner).
  // NOT: `disabled: true` KULLANILMAZ — dnd-kit sarmalayıcıya
  // `aria-disabled` bırakıyor ve içindeki GERİ AL düğmesi tıklanamaz hale
  // geliyordu. Bunun yerine sürükleme tutamacı görünmez bir öğeye bağlanır,
  // böylece yer tutucu sürüklenemez ama düğmeler çalışır.
  const isDetached = !!ctx.detachedSubIds?.has(sub.id);
  const { ref: sortRef, handleRef, isDragSource } = useSortable({
    id: sub.id,
    index,
    group,
    type: group,
    accept: [group],
    data: {
      kind: "subtask", taskId: ctx.taskId, parentId,
      // ÇOKLU SEÇİM: seçili alt görevlerden biri sürüklenirse HEPSİ taşınır.
      selectedIds: ctx.selectMode && ctx.selectedIds?.has(sub.id) ? [...ctx.selectedIds] : null,
    },
  });
  const {
    taskId, numbers, selectMode, selectedIds, highlight,
    onToggle, onOpenMenu, onSelectToggle, onSetSubDates,
    addChildParentId, newChildText, setNewChildText, commitAddChild, cancelAddChild,
  } = ctx;
  const sStyle = sub.renk && subIsOverdue(sub) ? subtaskStyle({ status: "pending" }) : subtaskStyle(sub);
  const sOverdue = subIsOverdue(sub);
  const isDone = sub.done || sub.status === "done";
  const number = numbers[sub.id];
  const selected = selectedIds.has(sub.id);
  const hasChildren = Array.isArray(sub.children) && sub.children.length > 0;
  // İç görev istatistiği (rozet) + katla/aç durumu (localStorage'da kalıcı).
  const childStats = hasChildren
    ? (() => {
        const flat = flattenSubs(sub.children);
        return { done: flat.filter((s) => s.done || s.status === "done").length, total: flat.length };
      })()
    : { done: 0, total: 0 };
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SUBTASK_COLLAPSE_KEY_PREFIX + sub.id) === "1";
    } catch (e) {
      return false;
    }
  });
  const toggleCollapse = (e) => {
    if (e) e.stopPropagation();
    setCollapsed((v) => {
      const nv = !v;
      try {
        if (nv) localStorage.setItem(SUBTASK_COLLAPSE_KEY_PREFIX + sub.id, "1");
        else localStorage.removeItem(SUBTASK_COLLAPSE_KEY_PREFIX + sub.id);
      } catch (er) { console.warn("[SubtaskRow] collapse save failed:", er); }
      return nv;
    });
  };
  const rowRef = useRef(null);
  const persistTimer = useRef(null);
  const longPress = useRef(null);

  const [savedSize] = useState(() => {
    try {
      const raw = localStorage.getItem(SUBTASK_SIZE_KEY_PREFIX + sub.id);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.width || parsed.height)) return parsed;
      }
    } catch (e) { console.warn("[SubtaskRow] size read failed:", e); }
    return null;
  });

  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const inlineW = el.style.width;
      const inlineH = el.style.height;
      if (!inlineW && !inlineH) return;
      const size = { width: el.offsetWidth, height: el.offsetHeight };
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => {
        try {
          localStorage.setItem(SUBTASK_SIZE_KEY_PREFIX + sub.id, JSON.stringify(size));
        } catch (e) { console.warn("[SubtaskRow] size save failed:", e); }
      }, 250);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [sub.id]);

  const openMenuAt = (x, y) => onOpenMenu(sub.id, x, y);

  if (isDetached) {
    return (
      <div ref={sortRef} data-testid={`subtask-item-${taskId}-${sub.id}`} data-dndprobe="off">
        <span ref={handleRef} aria-hidden="true" className="hidden" />
        <DetachedSubtaskPlaceholder
          taskId={taskId}
          sub={sub}
          displayNumber={number}
          onDock={() => ctx.onDockSubtask?.(sub.id)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openMenuAt(e.clientX, e.clientY);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={sortRef}
      className="space-y-1 relative"
      data-dragsource={isDragSource ? "true" : "false"}
      data-testid={`subtask-item-${taskId}-${sub.id}`}
    >
      {/* FAZ 2B — "İÇİNE" noktası SATIRIN kendisine göre konumlanır.
          Önceden dış sarmalayıcıya göre `top-1/2` idi; çocukları genişletilmiş
          satırlarda sarmalayıcı çocukları da kapsadığı için nokta satırdan
          kayıp çocukların ortasında kalıyordu (kullanıcının bildirdiği hata).
          Nokta satır kabının DIŞINDA kardeş olarak durur: satırdaki
          `overflow:auto` + `resize` yeteneği bozulmaz, nokta da kırpılmaz. */}
      <div className="relative">
        <NestDropDot taskId={taskId} subId={sub.id} />
        {sOverdue && <GecikmeIsareti satir testId={`subtask-overdue-${taskId}-${sub.id}`} />}
        <div
        ref={rowRef}
        data-renk={sub.renk || "mavi"}
        style={{
          resize: "both",
          overflow: "auto",
          minHeight: 32,
          minWidth: 180,
          maxWidth: "100%",
          ...(savedSize?.width ? { width: savedSize.width } : {}),
          ...(savedSize?.height ? { height: savedSize.height } : {}),
          ...(renkStili(sub.renk) || {}),
        }}
        className={`subtask-resizable flex items-start gap-1.5 group/sub rounded px-1 py-0.5 border ${sStyle.border} ${sStyle.bg} transition-colors${selected ? " ring-1 ring-violet-400/70 bg-violet-500/10" : ""}`}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openMenuAt(e.clientX, e.clientY);
        }}
        onTouchStart={(e) => {
          // Sürükleme tutamacında UZUN BASMA menüsü açılmaz (dnd-kit 250 ms
          // basılı tutma ister; 500 ms'de menü açılırsa sürükleme bozulur).
          if (e.target?.closest?.('[data-testid^="subtask-drag-"]')) return;
          const touch = e.touches[0];
          longPress.current = setTimeout(() => {
            if (document.querySelector('[data-dragsource="true"]')) return;
            openMenuAt(touch.clientX, touch.clientY);
            if (navigator.vibrate) navigator.vibrate(30);
          }, 500);
        }}
        onTouchEnd={() => clearTimeout(longPress.current)}
        onTouchMove={() => clearTimeout(longPress.current)}
        data-testid={`subtask-row-${taskId}-${sub.id}`}
      >
        {hasChildren ? (
          <button
            onClick={toggleCollapse}
            data-testid={`subtask-collapse-${taskId}-${sub.id}`}
            title={collapsed ? "Genişlet" : "Katla"}
            aria-label={collapsed ? "Genişlet" : "Katla"}
            className="shrink-0 mt-0.5 h-4 w-3 flex items-center justify-center text-violet-300 hover:text-violet-100 transition-colors"
          >
            {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        ) : (
          <span className="shrink-0 w-3" aria-hidden="true" />
        )}
        <button
          ref={handleRef}
          data-testid={`subtask-drag-${taskId}-${sub.id}`}
          title="Sürükle sırala"
          aria-label="Sürükle sırala"
          className="opacity-60 group-hover/sub:opacity-100 focus:opacity-100 shrink-0 mt-0.5 h-5 w-4 -ml-0.5 flex items-center justify-center text-sertex-cyan/80 hover:text-sertex-cyan cursor-grab active:cursor-grabbing transition-opacity touch-none"
        >
          <GripVertical className="h-3 w-3" />
        </button>
        <button
          onClick={() => onToggle(sub.id, !isDone)}
          data-testid={`subtask-check-${taskId}-${sub.id}`}
          className={`mt-0.5 h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${
            isDone
              ? "border-emerald-400 bg-emerald-500/40"
              : sOverdue
              ? "border-rose-400 hover:bg-rose-500/10"
              : sub.status === "paused"
              ? "border-yellow-400 hover:bg-yellow-500/10"
              : "border-sertex-cyan/40 hover:border-sertex-cyan hover:bg-sertex-cyan/10"
          }`}
        >
          {isDone && <Check className="h-2.5 w-2.5 text-white" />}
        </button>
        <div
          className={`flex-1 min-w-0 ${selectMode ? "cursor-pointer" : ""}`}
          onClick={selectMode ? () => onSelectToggle(sub.id) : undefined}
        >
          {sub.status === "paused" && (
            <div className={`hud-text flex items-center gap-1 ${sStyle.accent}`}>
              <Pause className="h-2.5 w-2.5" /> BEKLEMEDE
            </div>
          )}
          <span className={`text-xs font-mono ${isDone ? "text-sertex-textMuted line-through" : "text-sertex-text"}`}>
            {number != null && (
              <span
                className={`inline-flex items-center mr-1 tabular-nums font-semibold ${
                  isDone ? "text-sertex-textMuted"
                    : sOverdue ? "text-rose-300"
                    : sub.status === "paused" ? "text-yellow-300"
                    : sub.number_pinned ? "text-amber-300"
                    : "text-sertex-cyan"
                }`}
                data-testid={`subtask-number-${taskId}-${sub.id}`}
                title={sub.number_pinned ? "Sıra numarası sabit" : undefined}
              >
                {isDone ? "✓" : `${number}.`}
                {sub.number_pinned && !isDone && (
                  <Anchor className="h-2.5 w-2.5 ml-0.5 text-amber-300" data-testid={`subtask-number-pinned-${taskId}-${sub.id}`} />
                )}
              </span>
            )}
            <Highlight text={sub.text} query={highlight} />
            {hasChildren && (
              <span
                className={`ml-1.5 text-[9px] font-mono px-1 rounded ${childStats.done === childStats.total ? "text-emerald-300 bg-emerald-500/15" : "text-violet-200 bg-violet-500/15"}`}
                data-testid={`subtask-childbadge-${taskId}-${sub.id}`}
                title={`${childStats.done}/${childStats.total} iç görev tamamlandı${collapsed ? " (katlı)" : ""}`}
              >
                ↳ {childStats.done}/{childStats.total}
                {collapsed && " ▸"}
              </span>
            )}
          </span>
          {sub.due_date && (
            <div className={`hud-text flex items-center gap-1 mt-0.5 ${sOverdue ? "text-rose-300" : sub.reminder_fired ? "text-sertex-textMuted" : "text-sertex-cyan"}`}>
              <Clock className="h-2.5 w-2.5" />
              {new Date(sub.due_date).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              {!isDone && (
                sub.reminder_fired ? (
                  <span title="Hatırlatma verildi" className="ml-1 opacity-70">🔔 ✓</span>
                ) : (
                  <Bell className="h-2.5 w-2.5 ml-1 opacity-70" title="Zamanı gelince hatırlatılacak" />
                )
              )}
            </div>
          )}
          <SubtaskDates sub={sub} taskId={taskId} onSetDates={onSetSubDates} />
        </div>
        {selectMode ? (
          <button
            onClick={(e) => { e.stopPropagation(); onSelectToggle(sub.id); }}
            data-testid={`subtask-select-${taskId}-${sub.id}`}
            title="Seç"
            aria-label="Seç"
            className={`shrink-0 h-5 w-5 flex items-center justify-center rounded-full border transition-all ${selected ? "border-violet-400 bg-violet-500/50 text-white" : "border-violet-400/50 text-violet-300 hover:bg-violet-500/15"}`}
          >
            {selected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              openMenuAt(rect.left - 200, rect.bottom + 4);
            }}
            data-testid={`subtask-menu-${taskId}-${sub.id}`}
            title="Alt görev menüsü"
            aria-label="Alt görev menüsü"
            className="opacity-0 group-hover/sub:opacity-100 focus:opacity-100 shrink-0 h-5 w-5 flex items-center justify-center border border-sertex-cyan/25 hover:border-sertex-cyan hover:bg-sertex-cyan/15 rounded text-sertex-cyan transition-all"
          >
            <MoreVertical className="h-3 w-3" />
          </button>
        )}
      </div>
      </div>

      {/* İç içe alt görev ekleme kutusu — menüden "Alt görev ekle" ile açılır */}
      {addChildParentId === sub.id && (
        <div className="ml-4 pl-1.5 border-l border-violet-400/25 flex items-center gap-1.5">
          <CornerDownRight className="h-3 w-3 text-violet-300 shrink-0" />
          <input
            value={newChildText}
            onChange={(e) => setNewChildText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newChildText.trim()) commitAddChild(sub.id);
              else if (e.key === "Escape") cancelAddChild();
            }}
            onBlur={() => commitAddChild(sub.id)}
            autoFocus
            placeholder="İç içe alt görev... (Enter: ekle · Esc: iptal)"
            data-testid={`subtask-addchild-input-${sub.id}`}
            className="flex-1 bg-transparent border-b border-violet-400/30 focus:border-violet-400 outline-none text-xs font-mono text-sertex-text placeholder:text-sertex-textMuted/60 pb-0.5"
          />
        </div>
      )}

      {/* Çocuklar — özyinelemeli, girintili (katlıysa gizli) */}
      {hasChildren && !collapsed && (
        <div className="ml-4 pl-1.5 border-l border-sertex-cyan/15">
          <SubtaskTree nodes={sub.children} parentId={sub.id} depth={depth + 1} ctx={ctx} />
        </div>
      )}
    </div>
  );
}

// AŞAMA 13 (Düzeltme 2) — satır izolasyonu: `ctx` TaskCard içinde useMemo ile
// stabil tutulduğu için aynı alt görev değişmediğinde satır yeniden çizilmez.
export const SubtaskRow = memo(SubtaskRowBase);
